import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PlaneLanding, PlaneTakeoff, AlertTriangle, MapPin, User, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Types ---
export interface MovementCardData {
  tailNumber: string
  movementType: 'arriving' | 'departing'
  isCompleted: boolean
  
  timeDisplay: string | null
  typeCode: string | null
  confidence: 'confirmed' | 'tentative' | 'predicted' | null
  airportIcao: string | null
  airportName: string | null
  providerDisplay: string | null
  flightReference: string | null

  clientName: string | null
  contractLabel: string | null
  assignedSpace: string | null

  isReady: boolean
  hasSpace: boolean
  hasSchedule: boolean
}

// --- Mock Data States ---
const baseArrival: MovementCardData = {
  tailNumber: 'N12345',
  movementType: 'arriving',
  isCompleted: false,
  timeDisplay: '15:25',
  typeCode: 'G550',
  confidence: 'confirmed',
  airportIcao: 'KLAX',
  airportName: 'Los Angeles Intl',
  providerDisplay: 'FlightAware',
  flightReference: 'FA123',
  clientName: null,
  contractLabel: null,
  assignedSpace: null,
  isReady: false,
  hasSpace: false,
  hasSchedule: true
}

const STATES = {
  level0: { 
    ...baseArrival, 
    description: "Level 0 (MVP): Schedule only. We know it's coming, but it's not registered to a client in our system yet." 
  },
  level1: { 
    ...baseArrival, 
    clientName: "Acme Aviation",
    contractLabel: "Transient",
    isReady: true,
    description: "Level 1: Registered. We know who it is and their contract, but haven't assigned a parking space yet."
  },
  level2: { 
    ...baseArrival, 
    clientName: "Acme Aviation",
    contractLabel: "Transient",
    assignedSpace: "Ramp East · Spot 4",
    isReady: true,
    hasSpace: true,
    description: "Level 2: Fully Ready. Registered and assigned a space. Ready for check-in upon landing."
  },
  level3: { 
    ...baseArrival, 
    clientName: "Acme Aviation",
    contractLabel: "Transient (Checked In)",
    assignedSpace: "Ramp East · Spot 4",
    isReady: true,
    hasSpace: true,
    isCompleted: true,
    description: "Level 3: Completed. Aircraft has arrived and is checked in."
  }
}

// --- Variation 1: Stacked Classic ---
// Focuses on vertical hierarchy, easy reading top-to-bottom. Uses full width.
function MovementCardV1({ data }: { data: MovementCardData }) {
  const isArrival = data.movementType === 'arriving'
  const accentColor = isArrival ? 'text-blue-600' : 'text-violet-600'
  const bgAccent = isArrival ? 'bg-blue-50' : 'bg-violet-50'
  const Icon = isArrival ? PlaneLanding : PlaneTakeoff

  return (
    <Card className={cn("overflow-hidden max-w-sm transition-opacity border-l-4", isArrival ? 'border-l-blue-500' : 'border-l-violet-500', data.isCompleted && "opacity-60")}>
      <CardContent className="p-4">
        {/* Header: Core identity + Time */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-2xl font-black text-slate-900 tracking-tight leading-none">{data.timeDisplay}</span>
              {data.typeCode && <Badge variant="secondary" className="text-xs">{data.typeCode}</Badge>}
            </div>
            <div className="flex items-center gap-2 font-bold text-lg text-slate-700 tracking-tight">
              <span>{data.tailNumber}</span>
              <span className="text-slate-400 font-normal text-sm">|</span>
              <span className="flex items-center text-sm font-medium text-slate-500">
                <Icon className={cn("h-4 w-4 mr-1", accentColor)} />
                {data.airportIcao}
              </span>
            </div>
          </div>
          <div className="text-right">
             {data.isCompleted ? (
               <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Completed</Badge>
             ) : (
               <Badge variant="outline" className={cn("border-transparent font-medium", bgAccent, accentColor)}>
                 {isArrival ? 'Inbound' : 'Outbound'}
               </Badge>
             )}
          </div>
        </div>

        {/* Body: Client & Space details (Progressive disclosure) */}
        <div className="space-y-2 mb-4 text-sm">
          {data.clientName ? (
            <div className="flex items-center text-slate-700">
              <User className="h-4 w-4 mr-2 text-slate-400" />
              <span className="truncate">{data.clientName}</span>
              {data.contractLabel && <span className="text-slate-400 ml-1">({data.contractLabel})</span>}
            </div>
          ) : null}
          
          {data.assignedSpace ? (
            <div className="flex items-center text-slate-700">
              <MapPin className="h-4 w-4 mr-2 text-slate-400" />
              <span>{data.assignedSpace}</span>
            </div>
          ) : null}
        </div>

        {/* Footer: Warnings and Actions */}
        <div className="flex flex-col gap-2">
          {!data.isReady && !data.isCompleted && (
            <div className="flex items-center text-red-600 text-xs font-semibold bg-red-50 p-2 rounded border border-red-100">
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
              Not registered in system
            </div>
          )}
          {data.isReady && !data.hasSpace && !data.isCompleted && (
            <div className="flex items-center text-amber-600 text-xs font-semibold bg-amber-50 p-2 rounded border border-amber-100">
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
              Needs parking assignment
            </div>
          )}
          
          {!data.isCompleted && (
             <div className="flex items-center gap-2 mt-1">
                {!data.isReady ? (
                  <Button size="sm" className="w-full bg-slate-900 text-white">Register</Button>
                ) : (
                  <>
                    <Button size="sm" className={cn("flex-1", isArrival ? "bg-blue-600 hover:bg-blue-700" : "bg-violet-600 hover:bg-violet-700")}>
                      Check In
                    </Button>
                    {!data.hasSpace && (
                      <Button size="sm" variant="outline" className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50">
                        Assign Space
                      </Button>
                    )}
                  </>
                )}
             </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// --- Variation 2: Compact Inline ---
// Uses a 2-column internal grid for details, packs more info into less vertical space.
function MovementCardV2({ data }: { data: MovementCardData }) {
  const isArrival = data.movementType === 'arriving'
  const isReady = data.isReady
  const isWarning = (!isReady || !data.hasSpace) && !data.isCompleted

  return (
    <Card className={cn(
        "max-w-sm transition-all", 
        data.isCompleted && "opacity-50 bg-slate-50",
        isWarning && "border-amber-300 shadow-sm"
    )}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
           <div className="flex items-baseline gap-2">
             <span className="font-mono font-bold text-base text-slate-800">{data.timeDisplay}</span>
             <span className="font-bold text-base tracking-tight">{data.tailNumber}</span>
           </div>
           {data.typeCode && <span className="text-xs font-mono px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{data.typeCode}</span>}
        </div>

        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs mb-3">
          <div className="col-span-2 flex items-center text-slate-600 mb-1">
             {isArrival ? 'From: ' : 'To: '} <strong className="ml-1 text-slate-800">{data.airportIcao}</strong>
             <span className="text-slate-400 ml-1 truncate">— {data.airportName}</span>
          </div>

          <div className="flex items-center text-slate-600 truncate">
             <User className="h-3 w-3 mr-1 opacity-60" />
             {data.clientName ? <span className="truncate">{data.clientName}</span> : <span className="text-slate-400 italic">Unknown</span>}
          </div>
          
          <div className="flex items-center text-slate-600 truncate">
             <MapPin className="h-3 w-3 mr-1 opacity-60" />
             {data.assignedSpace ? <span className="font-medium text-slate-800 truncate">{data.assignedSpace}</span> : <span className="text-amber-600 font-medium italic">Unassigned</span>}
          </div>
        </div>

        {!data.isCompleted && (
            <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 mt-1">
                <div className="text-xs font-medium">
                    {!isReady ? (
                        <span className="text-red-600 flex items-center"><AlertTriangle className="h-3 w-3 mr-1" />Not registered</span>
                    ) : !data.hasSpace ? (
                        <span className="text-amber-600 flex items-center"><AlertTriangle className="h-3 w-3 mr-1" />Needs space</span>
                    ) : (
                        <span className="text-green-600 flex items-center"><CheckCircle2 className="h-3 w-3 mr-1" />Ready</span>
                    )}
                </div>
                <div className="flex gap-2">
                    {!isReady ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-slate-300">Register</Button>
                    ) : (
                        <>
                           {!data.hasSpace && <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-700 hover:bg-amber-50">Assign</Button>}
                           <Button size="sm" className="h-7 text-xs bg-slate-900 text-white">Check In</Button>
                        </>
                    )}
                </div>
            </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Variation 3: Status-Forward Border ---
// Uses left-border for direction, heavily emphasizes action buttons inline with header.
function MovementCardV3({ data }: { data: MovementCardData }) {
  const isArrival = data.movementType === 'arriving'
  const isReady = data.isReady
  
  // Left border color defines direction
  const borderCol = data.isCompleted ? 'border-l-slate-300' 
                  : isArrival ? 'border-l-blue-500' 
                  : 'border-l-violet-500'

  return (
    <Card className={cn(
        "max-w-sm rounded-md overflow-hidden border-l-4", 
        borderCol,
        data.isCompleted && "opacity-60 bg-slate-50 border-slate-200"
    )}>
      <CardContent className="p-0">
        <div className="p-3">
            <div className="flex justify-between items-start mb-2">
                <div>
                    <div className="flex items-center gap-1.5">
                        <span className="font-bold text-lg leading-none">{data.tailNumber}</span>
                        {data.typeCode && <span className="text-[10px] uppercase font-bold text-slate-500">{data.typeCode}</span>}
                    </div>
                    <div className="text-sm font-medium text-slate-700 mt-1">
                        {data.timeDisplay} <span className="text-slate-400 mx-1">|</span> {isArrival ? '←' : '→'} {data.airportIcao}
                    </div>
                </div>

                {/* Inline Action block for immediacy */}
                {!data.isCompleted && (
                    <div className="flex flex-col items-end gap-1">
                        {!isReady ? (
                             <Button size="sm" className="h-7 bg-red-600 hover:bg-red-700 text-xs">Register</Button>
                        ) : (
                             <Button size="sm" className={cn("h-7 text-xs", isArrival ? "bg-blue-600 hover:bg-blue-700" : "bg-violet-600 hover:bg-violet-700")}>
                                Check In
                             </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Contextual data block */}
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 text-xs text-slate-600 mt-3 bg-slate-50/50 p-2 rounded border border-slate-100">
                <span className="font-medium text-slate-400">Client:</span>
                <span className="truncate">{data.clientName || <span className="text-red-500 italic">Missing</span>}</span>
                
                <span className="font-medium text-slate-400">Space:</span>
                <span className="truncate">
                    {data.assignedSpace ? (
                        <span className="font-medium text-slate-800">{data.assignedSpace}</span>
                    ) : (
                        <span className="text-amber-600 flex items-center">
                            Unassigned
                            {data.isReady && !data.isCompleted && (
                                <button className="ml-2 underline hover:text-amber-800 cursor-pointer text-[10px] uppercase tracking-wider font-bold">Assign</button>
                            )}
                        </span>
                    )}
                </span>
            </div>
            
            {/* Minimal Warning Strip if needed */}
            {!data.isReady && !data.isCompleted && (
                 <div className="mt-2 text-[10px] font-bold text-red-600 uppercase tracking-widest text-center py-1 bg-red-50 rounded">
                     Action Required: Registration
                 </div>
            )}
        </div>
      </CardContent>
    </Card>
  )
}

export function MovementCardDemos() {
  const variations = [
    { title: "Variation 1: Stacked Classic", Component: MovementCardV1, desc: "Traditional vertical hierarchy. Safe, familiar, clearly separated sections." },
    { title: "Variation 2: Compact Inline", Component: MovementCardV2, desc: "Denser horizontal layout. Focuses on grid alignment to save vertical height." },
    { title: "Variation 3: Status-Forward Border", Component: MovementCardV3, desc: "Uses thick left-border for direction. Prioritizes actions by putting them top-right." }
  ]

  const statesList = [
      { key: 'level0', ...STATES.level0 },
      { key: 'level1', ...STATES.level1 },
      { key: 'level2', ...STATES.level2 },
      { key: 'level3', ...STATES.level3 }
  ]

  return (
    <div className="min-h-screen bg-slate-100 p-8 pb-20">
      <div className="max-w-6xl mx-auto space-y-12">
        <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">Movement Card Variations & Evolution</h1>
            <p className="text-slate-600 max-w-3xl">
                Demonstrating how a single aircraft movement card evolves from Level 0 (MVP/Schedule-only) to Level 3 (Completed).
                Below are 3 different design approaches for the compact card format requested for the FBO daily operations board.
            </p>
        </div>

        {variations.map((v, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div className="mb-6 border-b border-slate-100 pb-4">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <span className="bg-slate-900 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-sm font-black">{idx + 1}</span>
                        {v.title}
                    </h2>
                    <p className="text-slate-500 mt-1">{v.desc}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {statesList.map(state => (
                        <div key={state.key} className="flex flex-col">
                            <div className="mb-4 h-24">
                                <Badge variant="outline" className="mb-2 bg-slate-50">{state.key.toUpperCase()}</Badge>
                                <p className="text-xs text-slate-500 leading-relaxed">{state.description}</p>
                            </div>
                            <div className="flex-1">
                                <v.Component data={state} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ))}
      </div>
    </div>
  )
}
